import cx from "classnames";
import { getConsole } from "lib/getConsole";
import _ from "lodash";
import React from "react";
import styles from "./ElevationGraph.scss";
const console = getConsole("rr", "ElevationGraph");

// build an x-y mapping of distance to elevation, with a resolution of N points.
// roughly O(n)
export const getGraphData = _.memoize((points, resolution) => {
  console.log("‼️ getGraphData calculation ran");
  if (resolution > points.length) {
    throw Error("can't upconvert fewer points than resolution");
  }
  // even if resolution === points.length, there isn't an equal distance between
  // points, so we have to sample and interpolate anyways
  const xMin = points[0].distance;
  const xMax = points[points.length - 1].distance;
  const xRange = xMax - xMin;
  // we can safely assume that the points here are ordered by distance
  let lastExaminedPoint = 0;
  return _.range(resolution) // generate a range of 0 to resolution -1
    .map((i) => {
      const distance = (i / (resolution - 1)) * xRange;
      // find the first point in the data where point.distance >= distance
      while (points[lastExaminedPoint].distance < distance) {
        lastExaminedPoint += 1;
      }
      // no interpolation if distance is an exact match
      if (distance === points[lastExaminedPoint].distance) {
        return points[lastExaminedPoint].ele;
      } else {
        const prevPoint = points[lastExaminedPoint - 1];
        const nextPoint = points[lastExaminedPoint];
        const range = nextPoint.distance - prevPoint.distance;
        // distance-weighted average
        return (
          (prevPoint.ele * (range - (distance - prevPoint.distance))) / range +
          (nextPoint.ele * (range - (nextPoint.distance - distance))) / range
        );
      }
    });
});

// generate unique clip path ids
let id = 0;
const genClipPathId = () => {
  id += 1;
  return `ele-clip-path-${id}`;
};

// in order to properly render the elevation graph, we need to convert x-axis pixel values
// to elevations. We want the x-axis to represent distance, so we need to be able to
// efficiently look up a point by distance and grab its elevation.
export class ElevationGraph extends React.PureComponent {
  yScale = (y) => {
    const { stats, height } = this.props;
    const range = stats.ele._max - stats.ele._min;
    const yPad = 20; // y padding in pixels
    return ((y - stats.ele._min) * (height - 2 * yPad)) / range + yPad;
  };

  render() {
    const { width, progress, points, height, fill, timeIndexed, stats, resolution, paused } = this.props;

    return (
      <div className={styles.ElevationGraph} style={{ width: width || "100%" }}>
        <AreaPlot
          points={points}
          timeIndexed={timeIndexed}
          fill={fill}
          stats={stats}
          width="100%"
          resolution={resolution}
          height={height}
          progress={progress}
          yScale={this.yScale}
        />
        {progress && (
          <HighlightPoint
            paused={paused}
            progress={progress}
            height={height}
            stats={stats}
            yScale={this.yScale}
            timeIndexed={timeIndexed}
          />
        )}
      </div>
    );
  }
}

// interesting effect: highlighted point needs to be a seperate component for
// performance reasons, but it would be nice to share the same viewBox...
class AreaPlot extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = { clipId: genClipPathId() };
  }

  render() {
    const { width, height, resolution, fill, points, yScale, progress, timeIndexed, stats } = this.props;
    const { clipId } = this.state;
    const graphData = getGraphData(points, resolution);
    const fractionalDistance =
      progress && timeIndexed[Math.floor(progress * timeIndexed.length)].distance / stats.distance;
    return (
      <div>
        <svg width={width} height={height} viewBox={`0 0 ${resolution - 1} ${height}`} preserveAspectRatio="none">
          <defs>
            <ClipPath id={clipId} height={height} graphData={graphData} yScale={yScale} resolution={resolution} />
          </defs>
          <g>
            <rect x="0" y="0" fill={fill || "#ddd"} width={resolution} height={height} clipPath={`url(#${clipId})`} />
            {progress && (
              <rect
                x="0"
                y="0"
                fill="#bdbdbd"
                width={resolution * fractionalDistance}
                height={height}
                clipPath={`url(#${clipId})`}
              />
            )}
          </g>
        </svg>
      </div>
    );
  }
}

// seperate component for performance reasons - SHOULD ONLY RENDER ONCE
class ClipPath extends React.PureComponent {
  render() {
    const { height, graphData, yScale, resolution, id } = this.props;
    const convertYtoPx = (e) => height - Math.floor(yScale(e));
    const dataToRender = graphData.map((e, i) => i + "," + convertYtoPx(e));
    console.log("‼️ clipPath rerender");
    return (
      <clipPath id={id}>
        <path d={`M0,${height}L` + dataToRender.join("L") + `L${resolution - 1},${height}Z`} />
      </clipPath>
    );
  }
}

const HighlightPoint = ({ progress, height, yScale, stats, timeIndexed, paused }) => {
  const { ele, distance } = timeIndexed[Math.floor(progress * timeIndexed.length)];
  const top = height - yScale(ele);
  const left = (distance / stats.distance) * 100 + "%";

  return (
    <div className={styles.HighlightPoint}>
      <div className={cx(styles.highlightCircle, paused && styles.pointPaused)} style={{ left, top }} />
    </div>
  );
};
